/**
 * lineageApi.ts
 *
 * Typed client for the search service's lineage read: the breadcrumb surface for
 * one sphere (ancestors, direct children, per-child child counts) served from the
 * indexed center lineage rather than a graph context traversal.
 * See /standards/backend/read-routing.md ("Breadcrumbs / ancestry").
 */
import type { ApiClient } from '@center-technologies/shared-api-clients';

export interface LineageNodeDto {
  id: string;
  name: string | null;
  organizationId: string;
  schemaSystemName: string;
  imageId: string | null;
  glyph: string | null;
  glyphColor: Record<string, unknown> | null;
  /** Indexed lineage depth; null when the sphere has not been re-indexed with lineage. */
  depth: number | null;
}

export interface LineageChildDto extends LineageNodeDto {
  /** Number of the child's own children the caller may view (drives the expand chevron). */
  childCount: number;
}

export interface SphereLineageDto {
  sphere: LineageNodeDto;
  /** Root first, immediate parent last; ancestors the caller may not discover are omitted. */
  ancestors: LineageNodeDto[];
  /** Direct children the caller may view, ordered by name. */
  children: LineageChildDto[];
}

/** The center-hierarchy context on the graph context executor — what the search read replaced. */
export const CENTER_HIERARCHY_CONTEXT = 'center_hierarchy_context';

function isNotFound(error: unknown): boolean {
  return (error as { response?: { status?: number } })?.response?.status === 404;
}

/**
 * Fetch the lineage surface for a sphere from the search index. A 404 means the index cannot
 * serve this sphere for the caller — not indexed yet, or indexed badly — which the graph can
 * still answer, so with a `graphClient` the read falls back to it. Any other error, or a graph
 * that cannot see the sphere either, rejects with the original error so callers surface it
 * rather than render an empty hierarchy.
 */
export async function fetchSphereLineage(
  searchClient: ApiClient,
  sphereId: string,
  graphClient?: ApiClient | null
): Promise<SphereLineageDto> {
  try {
    return await searchClient.get<SphereLineageDto>(`/spheres/${encodeURIComponent(sphereId)}/lineage`);
  } catch (error) {
    if (!graphClient || !isNotFound(error)) throw error;
    const fromGraph = await fetchSphereLineageFromGraph(graphClient, sphereId);
    if (!fromGraph) throw error;
    return fromGraph;
  }
}

interface GraphNode {
  id: string;
  label?: string;
  glyph?: string;
  glyphColor?: Record<string, unknown> | null;
  edgeCount?: number;
  properties?: { name?: string; type?: string; imageId?: string; sphereId?: string };
  children?: GraphNode[];
  ancestors?: GraphNode[];
}

function graphNodeToLineageNode(node: GraphNode, organizationId: string): LineageNodeDto {
  return {
    id: node.id,
    name: node.properties?.name ?? null,
    organizationId,
    schemaSystemName: node.properties?.type ?? '',
    imageId: node.properties?.imageId || null,
    glyph: node.glyph || null,
    glyphColor: node.glyphColor ?? null,
    depth: null,
  };
}

/**
 * The same surface built from the graph: the center-hierarchy context executor for the sphere
 * and its direct children (with the permission-aware edge count as `childCount`), and the
 * ancestors route for the path. Returns null when the graph cannot see the sphere.
 */
export async function fetchSphereLineageFromGraph(
  graphClient: ApiClient,
  sphereId: string
): Promise<SphereLineageDto | null> {
  const [tree, ancestorData] = await Promise.all([
    graphClient.get<{ roots?: GraphNode[] }>(`/contexts/${CENTER_HIERARCHY_CONTEXT}/execute`, {
      params: { rootSphereId: sphereId, responseMode: 'tree', includeEdgeCount: true },
    }),
    // The ancestors route answers 404 for a sphere with no ancestors (an organization root).
    graphClient
      .get<{ ancestors?: GraphNode[] }>(`/spheres/${encodeURIComponent(sphereId)}/ancestors`)
      .catch((error) => (isNotFound(error) ? null : Promise.reject(error))),
  ]);

  const root = tree?.roots?.[0];
  if (!root) return null;
  const organizationId = root.properties?.sphereId ?? '';

  // The ancestors route nests root-outermost; each `ancestors[0]` is one level nearer the sphere.
  const ancestors: LineageNodeDto[] = [];
  let level = ancestorData?.ancestors;
  while (level?.length) {
    ancestors.push(graphNodeToLineageNode(level[0], organizationId));
    level = level[0].ancestors;
  }

  return {
    sphere: graphNodeToLineageNode(root, organizationId),
    ancestors,
    children: (root.children ?? []).map((child) => ({
      ...graphNodeToLineageNode(child, organizationId),
      childCount: child.edgeCount ?? 0,
    })),
  };
}
